import _capitalize from 'lodash/capitalize';
import enumsMixin from '@/mixins/enums';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import gql from 'graphql-tag';
import formatDate from 'date-fns/format';
import LocationSearch from '@/components/misc/LocationSearch';
import { bRowCols, bForm, bFormGroup } from '@src/plugins/vue-form-bricks/bootstrap-vue';
import { JobWhereManyInput } from '@src/models/where';

const { computed: { jobOrderByEnums } } = enumsMixin;

export const RelationJobFragment = gql`
  fragment RelationJobDetails on Job {
    id
    title
    atsID
    description
    excerpt
    linkedInSearchURL
    location {
      id
      name
      countryCode
    }
    department {
      id
      name
      icon
    }
    createdAt
    status
    customFields { key value }
    customFieldsHash
  }
`;

export const listSchema = {
  page: {
    title: 'Manage Jobs',
    breadcrumb: 'Jobs',
  },
  query: gql`
    query ListJobEntries($where: JobWhereManyInput, $order: JobOrderByInput) {
      company {
        id
        jobConnections(where: $where, order: $order) {
          edges {
            entry {
              ...RelationJobDetails
            }
          }
          total
        }
      }
    }
    ${RelationJobFragment}
  `,
  update({ company: { jobConnections: { edges, total } = {} } = {} } = {}) {
    return {
      edges: (Array.isArray(edges) ? edges : []).map(({ entry }) => {
        const e = JSON.parse(JSON.stringify(entry));
        const customLocation = e.customFieldsHash ? (e.customFieldsHash.location || null) : null;
        e.location = e.location || (customLocation ? { name: customLocation, id: customLocation } : null);
        return e;
      }),
      total,
    };
  },
  where: { status: 'ACTIVE' },
  whereOptions: JobWhereManyInput,
  order: 'CREATED_ASC',
  orderOptions: jobOrderByEnums,
  primaryKey: 'id',
  columns() {
    const columns = [
      { key: 'title', label: 'Name' },
      { key: 'atsID', label: 'ATS ID' },
      { key: 'department', label: 'Department' },
      { key: 'location', label: 'Location' },
    ];

    if (!this.modalOpts) {
      columns.push({ key: 'createdAt', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') });
      columns.push({ key: 'status', formatter: value => _capitalize(value) });
    }

    return columns;
  },
  option(createElement, { id, title }) {
    return createElement('div', { class: 'd-flex flex-row justify-content-start align-items-center' }, [
      createElement('span', { class: 'bg-dark text-white rounded-circle mr-1' }, [
        createElement('font-awesome-icon', { class: 'mx-1', props: { icon: 'users', fixedWith: true, size: 'sm' } }),
      ]),
      createElement('span', { class: 'd-block' }, title),
    ]);
  },
  emptyOptionValue() {
    return {
      icon: 'briefcase',
      title: this.placeholder || (this.many ? 'Select Jobs' : 'Select Job'),
    };
  },
};

const entryFragment = gql`
  fragment ManageJobDetails on Job {
    id
    title
    excerpt
    description

    linkedInSearchURL

    atsID
    atsURL

    department {
      id
      name
      icon
    }

    location {
      id
      name
      countryCode
    }

    image
    industry
    referralBonus
    customFieldsHash

    standardised {
      id
      title
      guessed
    }

    createdAt
    updatedAt
    status
  }
`;

export const editSchema = {
  page: {
    title() {
      return this.value && this.value.fullName
        ? `Edit Job - ${this.value.fullName}`
        : (this.entryID === 'new' ? 'New Job' : 'Edit Job');
    },
    breadcrumb() {
      return this.entryID === 'new' ? 'New' : 'Edit';
    },
  },
  apolloReadEntry: {
    query: gql`
      query ReadJobEntry($entryID: ID!) {
        company {
          id
          value: job(where:{ id: $entryID }) {
            ...ManageJobDetails
          }
        }
      }
      ${entryFragment}
    `,
  },
  apolloCreateEntry: {
    mutation: gql`
      mutation CreateJobEntry($entryID: ID!, $create: CreateJobInput!) {
        value: createJob(create: $create) {
          ...ManageJobDetails
        }
      }
      ${entryFragment}
    `,
  },
  apolloUpdateEntry: {
    mutation: gql`
      mutation UpdateJobEntry($entryID: ID!, $update: UpdateJobInput!) {
        value: updateJob(update: $update, where: { id: $entryID }) {
          ...ManageJobDetails
        }
      }
      ${entryFragment}
    `,
  },
  form: {
    $main: bForm({}, {
      contentBlock: bRowCols({ props: { cols: 12 } }, {
        title: bFormGroup({ props: { label: 'Title', 'label-for': 'title' } }, {
          is: 'b-input',
          props: { 'v-model': '$value.title' },
          domProps: { id: 'title' },
        }),
        description: bFormGroup({ props: { label: 'Description', 'label-for': 'description' } }, {
          is: 'b-form-textarea',
          props: {
            'no-resize': true,
            'no-auto-shrink': true,
            rows: 10,
            'v-model': '$value.description',
          },
          domProps: { id: 'description' },
        }),
      }),
      $hr1: { is: 'hr' },
      atsBlock: bRowCols({ props: { cols: 12, lg: 6 } }, {
        atsID: bFormGroup({ props: { label: 'ATS ID', 'label-for': 'atsID', 'label-cols-sm': 3 } }, {
          input: { is: 'b-input', props: { 'v-model': '$value.atsID' }, domProps: { id: 'atsID' } },
          help: {
            is: 'b-form-text',
            children: 'Set by your ATS & should not be edited by hand',
          },
        }),
        atsURL: bFormGroup({ props: { label: 'ATS URL', 'label-for': 'atsURL', 'label-cols-sm': 3 } }, {
          input: { is: 'b-input', props: { 'v-model': '$value.atsURL' }, domProps: { id: 'atsURL' } },
          help: {
            is: 'b-form-text',
            children: 'Enter a URL here to automatically redirect to an ATS',
          },
        }),
        // divide: { is: 'div', domProps: { class: 'w-100' } },
        // @TODO Add customFieldsHash editor here!
      }),
      $hr2: { is: 'hr' },
      detailsBlock: bRowCols({ props: { cols: 12, lg: 6 } }, {
        department: bFormGroup({ props: { label: 'Department', 'label-for': 'department', 'label-cols-sm': 3 } }, {
          is: EntryWhereSomeInput,
          props: {
            type: 'departments',
            title: 'Department',
            where: { status: 'ACTIVE' },
            'v-model': '$value.department',
          },
          domProps: { id: 'department' },
          on: {
            input(ctx, department) {
              ctx.setData('$value.department', department && department.id ? { id: department.id } : null);
            },
          },
        }),
        location: bFormGroup({ props: { label: 'Location', 'label-for': 'location', 'label-cols-sm': 3 } }, {
          is: LocationSearch,
          props: { value: '$value.location' },
          domProps: { id: 'location' },
          on: {
            input(ctx, location) {
              ctx.setData('$value.location', location && typeof location !== 'string' && location.id ? { id: location.id } : null);
            },
          },
        }),
        industry: bFormGroup({ props: { label: 'Industry', 'label-for': 'industry', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.industry' },
          domProps: { id: 'industry' },
        }),
        referralBonus: bFormGroup({ props: { label: 'Referral Bonus', 'label-for': 'referralBonus', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.referralBonus' },
          domProps: { id: 'referralBonus' },
        }),
        image: bFormGroup({ props: { label: 'Image', 'label-for': 'image', 'label-cols-sm': 3 } }, {
          is: 'b-input',
          props: { 'v-model': '$value.image' },
          domProps: { id: 'image' },
        }),
      }),
    }),
    $sidebar: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        return el('b-table', {
          class: 'status-table',
          props: {
            stacked: true,
            fields: [
              { key: 'id', label: 'ID' },
              { key: 'createdAt', label: 'Created At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'updatedAt', label: 'Updated At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'status', label: 'Status', formatter: value => _capitalize(`${value}`.toLowerCase()) },
            ],
            items: this.value ? [ this.value ] : [],
          },
        });
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/Jobs.js